/**
 * src/layouts/aurora/AuroraSidebar.jsx
 *
 * Aurora sidebar — clean, minimal-border navigation reading the same
 * NAV_GROUPS / SYSTEM_NAV catalogue as the Beta sidebar but with a flat
 * accordion: only the active group's items are visible at a time. No
 * nested borders, no rail-vs-grouped toggle, no resize handle, no
 * embedded user card (that lives in TopBar).
 *
 * Permissions, plan-gating, and prospect handling mirror the Beta
 * sidebar so the same routes appear / disappear consistently.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  NAV_GROUPS,
  SYSTEM_NAV,
  navModuleKey,
  navRoleAllowed,
  navReleaseAllowed,
} from '../../config/navConfig.js';
import { useReleasedFlags } from '../../utils/useReleaseFlag.js';
import { isProspect, getModulePermissions, resolveSeasuiteRole } from '../../utils/auth.js';
import { useMsrPublish } from '../../components/dashboard/controlPosture.js';

const SIDEBAR_WIDTH = 232;
const SYSTEM_KEY = '__system__';

function NavRow({ to, icon, label, active }) {
  return (
    <NavLink to={to} style={{ textDecoration: 'none' }}>
      {({ isActive }) => {
        const isOn = active != null ? active : isActive;
        return (
          <div
            className="aurora-nav-row"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              margin: '1px 8px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: isOn ? 600 : 500,
              color: isOn ? 'var(--accent-ink)' : 'var(--fg2)',
              background: isOn ? 'var(--accent)' : 'transparent',
              transition: 'background 0.12s, color 0.12s',
            }}
            onMouseEnter={(e) => {
              if (!isOn) {
                e.currentTarget.style.background = 'var(--bg3)';
                e.currentTarget.style.color = 'var(--fg)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isOn) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--fg2)';
              }
            }}
          >
            <span
              style={{
                width: 18,
                fontSize: 13,
                lineHeight: 1,
                textAlign: 'center',
                opacity: isOn ? 1 : 0.85,
              }}
            >
              {icon}
            </span>
            <span>{label}</span>
          </div>
        );
      }}
    </NavLink>
  );
}

function GroupHeader({ label, expanded, hasActive, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '14px 20px 6px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: 10,
        fontFamily: 'var(--font-mono)',
        fontWeight: 600,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        color: hasActive ? 'var(--fg2)' : 'var(--fg3)',
      }}
    >
      <span>{label}</span>
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 0.15s',
          opacity: 0.7,
        }}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
}

export default function AuroraSidebar({
  session,
  state: _state,
  canAdmin: canAdminProp,
  planGating,
  // GH #7057: derived by App from the user's own record (hrRecruitmentAccess).
  canHR = false,
}) {
  const location = useLocation();
  const role = session?.role || 'reader';
  const seasuiteRole = resolveSeasuiteRole(session);
  const prospect = isProspect(role);
  const modulePerm = getModulePermissions(session, seasuiteRole);
  // msr 4.10a: the Improvement entry follows the server's publish switch.
  const msrPublish = useMsrPublish();
  // RPDA-04: release flags the server resolved ON (fail-closed while loading).
  const releasedFlags = useReleasedFlags();

  const isAllowed = useCallback(
    (key) => (!planGating || planGating.isModuleAllowed(key)) && modulePerm[key] !== false,
    [planGating, modulePerm]
  );

  // Visible groups (NAV_GROUPS filtered by permissions, plus a synthetic System group)
  const groups = useMemo(() => {
    const navGroups = NAV_GROUPS.map((g) => ({
      key: g.key,
      label: g.label,
      items: (g.items || []).filter(
        (item) =>
          isAllowed(navModuleKey(item)) &&
          (!item.hrOnly || canHR) &&
          navReleaseAllowed(item, releasedFlags)
      ),
    })).filter((g) => g.items.length > 0);

    const sysItems = SYSTEM_NAV.filter(
      (item) =>
        isAllowed(navModuleKey(item)) &&
        navRoleAllowed(item, { canAdmin: canAdminProp, role, msrPublish, releasedFlags })
    ).concat([{ key: 'settings', path: '/settings', icon: 'ᛊ', label: 'Settings' }]);

    return sysItems.length > 0
      ? [...navGroups, { key: SYSTEM_KEY, label: 'System', items: sysItems }]
      : navGroups;
  }, [isAllowed, canAdminProp, role, msrPublish, canHR, releasedFlags]);

  // Which group contains the current route?
  const activeGroupKey = useMemo(() => {
    const found = groups.find((g) =>
      g.items.some((item) => location.pathname.startsWith(item.path))
    );
    return found ? found.key : (groups[0]?.key ?? null);
  }, [groups, location.pathname]);

  // Only one group expanded at a time. Default: the active group.
  const [openKey, setOpenKey] = useState(activeGroupKey);

  // When the user navigates to a route in a different group, expand that group automatically.
  React.useEffect(() => {
    if (activeGroupKey && activeGroupKey !== openKey) {
      setOpenKey(activeGroupKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupKey]);

  const toggle = useCallback((key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  return (
    <aside
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100%',
        background: 'var(--bg-sidebar, var(--bg2))',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {prospect ? (
          <>
            <div
              style={{
                padding: '14px 20px 6px',
                fontSize: 10,
                fontFamily: 'var(--font-mono)',
                fontWeight: 600,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                color: 'var(--fg3)',
              }}
            >
              Menu
            </div>
            <NavRow to="/gap" icon="ᚨ" label="Gap Analysis" />
          </>
        ) : (
          groups.map((group) => {
            const expanded = openKey === group.key;
            const hasActive = group.key === activeGroupKey;
            return (
              <div key={group.key}>
                <GroupHeader
                  label={group.label}
                  expanded={expanded}
                  hasActive={hasActive}
                  onToggle={() => toggle(group.key)}
                />
                {expanded &&
                  group.items.map((item) => (
                    <NavRow
                      key={item.key}
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      active={location.pathname.startsWith(item.path)}
                    />
                  ))}
              </div>
            );
          })
        )}
      </nav>
    </aside>
  );
}
